"use client";

import { useForgotPassword } from "@babackapp/private-api-client/client/use-forgot-password";
import { useLogin } from "@babackapp/private-api-client/client/use-login";
import { useLogout } from "@babackapp/private-api-client/client/use-logout";
import { useVerifyMfa } from "@babackapp/private-api-client/client/use-verify-mfa";
import { useRefresh } from "@babackapp/private-api-client/client/use-refresh";
import { useRegister } from "@babackapp/private-api-client/client/use-register";
import { useResendVerification } from "@babackapp/private-api-client/client/use-resend-verification";
import { useResetPassword } from "@babackapp/private-api-client/client/use-reset-password";
import { useVerifyEmail } from "@babackapp/private-api-client/client/use-verify-email";
import type {
	LoginRequestDto,
	LoginResponseDto,
	MfaVerifyRequestDto,
	RefreshResponseDto,
	RegisterRequestDto,
	UserDto,
} from "@babackapp/private-api-client/types";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import { useAuthInitialization } from "../hooks/useAuthInitialization";
import { useProactiveTokenRefresh } from "../hooks/useProactiveTokenRefresh";
import { useTokenRefreshInterceptor } from "../hooks/useTokenRefreshInterceptor";
import type { AuthContextType, AuthState, LoginResult } from "../types";
import {
	createAuthError,
	createAuthErrorInfo,
	debugError,
	debugLog,
	getErrorStatus,
	shouldDisconnectUser,
	tokenManager,
} from "../utils";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
	children: ReactNode;
	loginRoute?: string;
	defaultAuthenticatedRoute?: string;
	excludeRedirectPaths?: string[];
}

export function AuthProvider({
	children,
	loginRoute = "/auth/sign-in",
	defaultAuthenticatedRoute: _defaultAuthenticatedRoute = "/",
	excludeRedirectPaths = [],
}: AuthProviderProps) {
	const [state, setState] = useState<AuthState>({
		user: null,
		isAuthenticated: false,
		isLoading: true,
		isAuthenticating: true,
		error: null,
	});

	// Initialize mutation hooks
	const loginMutation = useLogin();
	const verifyMfaMutation = useVerifyMfa();
	const registerMutation = useRegister();
	const refreshMutation = useRefresh();
	const logoutMutation = useLogout();
	const verifyEmailMutation = useVerifyEmail();
	const forgotPasswordMutation = useForgotPassword();
	const resetPasswordMutation = useResetPassword();
	const resendVerificationMutation = useResendVerification();

	// Centralized refresh mutex to prevent concurrent refresh requests
	const refreshMutex = useRef<{
		isRefreshing: boolean;
		currentRefreshPromise: Promise<RefreshResponseDto> | null;
	}>({
		isRefreshing: false,
		currentRefreshPromise: null,
	});

	const refresh = useCallback(async () => {
		// If refresh is already in progress, return the existing promise
		if (
			refreshMutex.current.isRefreshing &&
			refreshMutex.current.currentRefreshPromise
		) {
			debugLog("Refresh already in progress, returning existing promise");
			return refreshMutex.current.currentRefreshPromise;
		}

		// Set the mutex flag
		refreshMutex.current.isRefreshing = true;

		debugLog("Starting token refresh");

		// Create the refresh promise and store it
		const refreshPromise = (async () => {
			try {
				const response = await refreshMutation.mutateAsync({
					refreshToken: null,
				});

				debugLog("Token refresh successful", { userId: response.user.id });
				tokenManager.setAccessToken(response.accessToken);
				setState((prev) => ({
					...prev,
					user: response.user,
					isAuthenticated: true,
					error: null,
				}));

				return response;
			} catch (error: any) {
				debugError("Token refresh failed", error);

				if (shouldDisconnectUser(error)) {
					debugError("Authentication error (401/403), disconnecting user");
					tokenManager.clearTokens();

					// Terminal failure - redirect to login immediately (full page reload will reset state)
					if (typeof window !== "undefined") {
						const currentPath = window.location.pathname;
						const isExcluded = excludeRedirectPaths.some(p => currentPath.startsWith(p));
						if (!isExcluded) {
							window.location.pathname = loginRoute;
						}
					}
				} else {
					// Network or server error - keep user authenticated but log the error
					debugError(
						"Refresh failed due to network/server error, keeping user authenticated",
						{ status: getErrorStatus(error) }
					);
				}

				throw error;
			} finally {
				// Always clear the mutex when done
				refreshMutex.current.isRefreshing = false;
				refreshMutex.current.currentRefreshPromise = null;
			}
		})();

		// Store the promise so other concurrent calls can use it
		refreshMutex.current.currentRefreshPromise = refreshPromise;

		return refreshPromise;
	}, [refreshMutation]);

	// Initialize authentication state
	useAuthInitialization(setState, refresh);

	// Set up proactive token refresh
	useProactiveTokenRefresh(state.isAuthenticated, refresh);

	// Inject refresh method into token refresh interceptor
	useTokenRefreshInterceptor(refresh);

	const openSession = (response: LoginResponseDto): UserDto => {
		if (!response.accessToken || !response.user) {
			throw new Error("Login response without a session");
		}

		debugLog("Login successful", { userId: response.user.id });
		tokenManager.setAccessToken(response.accessToken);

		setState((prev) => ({
			...prev,
			user: response.user!,
			isAuthenticated: true,
			isLoading: false,
			error: null,
		}));

		return response.user;
	};

	const failLogin = (error: unknown, fallbackMessage: string, code: string) => {
		debugError(fallbackMessage, error);
		setState((prev) => ({
			...prev,
			isLoading: false,
			error: createAuthErrorInfo(error, fallbackMessage, code),
		}));
		return createAuthError(error, fallbackMessage, code);
	};

	const login = async (credentials: LoginRequestDto): Promise<LoginResult> => {
		debugLog("Starting login", { email: credentials.email });
		try {
			setState((prev) => ({ ...prev, isLoading: true, error: null }));

			const response = await loginMutation.mutateAsync(credentials);

			if (response.mfaRequired && response.mfaToken) {
				debugLog("Login awaits the second factor");
				setState((prev) => ({ ...prev, isLoading: false, error: null }));
				return { status: "mfa_required", mfaToken: response.mfaToken };
			}

			return { status: "authenticated", user: openSession(response) };
		} catch (error) {
			throw failLogin(error, "Login failed", "LOGIN_FAILED");
		}
	};

	const verifyMfa = async (params: MfaVerifyRequestDto): Promise<UserDto> => {
		try {
			setState((prev) => ({ ...prev, isLoading: true, error: null }));

			const response = await verifyMfaMutation.mutateAsync(params);

			return openSession(response);
		} catch (error) {
			throw failLogin(error, "Verification failed", "MFA_VERIFICATION_FAILED");
		}
	};

	const register = async (data: RegisterRequestDto) => {
		try {
			setState((prev) => ({ ...prev, isLoading: true, error: null }));

			const response = await registerMutation.mutateAsync(data);

			setState((prev) => ({
				...prev,
				isLoading: false,
				error: null,
			}));

			return response;
		} catch (error) {
			const authErrorInfo = createAuthErrorInfo(
				error,
				"Registration failed",
				"REGISTRATION_FAILED",
			);

			setState((prev) => ({
				...prev,
				isLoading: false,
				isAuthenticating: false,
				error: authErrorInfo,
			}));
			throw createAuthError(
				error,
				"Registration failed",
				"REGISTRATION_FAILED",
			);
		}
	};

	const logout = async () => {
		debugLog("Starting logout");
		try {
			setState((prev) => ({ ...prev, isLoading: true, error: null }));

			await logoutMutation.mutateAsync({});

			debugLog("Logout successful");
			tokenManager.clearTokens();

			// No need to update auth state here, as we will force a page refresh
		} catch (error) {
			debugError("Logout failed", error);
		}

		// Force page refresh to clear all cached data (e.q react-query cache)
		window.location.pathname = loginRoute;
	};

	const verifyEmail = async (token: string) => {
		try {
			setState((prev) => ({ ...prev, isLoading: true, error: null }));

			await verifyEmailMutation.mutateAsync({ token });

			setState((prev) => ({
				...prev,
				isLoading: false,
				error: null,
			}));
		} catch (error) {
			const authErrorInfo = createAuthErrorInfo(
				error,
				"Email verification failed",
				"EMAIL_VERIFICATION_FAILED",
			);

			setState((prev) => ({
				...prev,
				isLoading: false,
				isAuthenticating: false,
				error: authErrorInfo,
			}));
			throw createAuthError(
				error,
				"Email verification failed",
				"EMAIL_VERIFICATION_FAILED",
			);
		}
	};

	const forgotPassword = async (email: string) => {
		try {
			setState((prev) => ({ ...prev, isLoading: true, error: null }));

			await forgotPasswordMutation.mutateAsync({ body: { email } });

			setState((prev) => ({
				...prev,
				isLoading: false,
				error: null,
			}));
		} catch (error) {
			const authErrorInfo = createAuthErrorInfo(
				error,
				"Password reset request failed",
				"FORGOT_PASSWORD_FAILED",
			);

			setState((prev) => ({
				...prev,
				isLoading: false,
				isAuthenticating: false,
				error: authErrorInfo,
			}));
			throw createAuthError(
				error,
				"Password reset request failed",
				"FORGOT_PASSWORD_FAILED",
			);
		}
	};

	const resetPassword = async (token: string, newPassword: string) => {
		try {
			setState((prev) => ({ ...prev, isLoading: true, error: null }));

			await resetPasswordMutation.mutateAsync({
				token,
				newPassword,
			});

			setState((prev) => ({
				...prev,
				isLoading: false,
				error: null,
			}));
		} catch (error) {
			const authErrorInfo = createAuthErrorInfo(
				error,
				"Password reset failed",
				"RESET_PASSWORD_FAILED",
			);

			setState((prev) => ({
				...prev,
				isLoading: false,
				isAuthenticating: false,
				error: authErrorInfo,
			}));
			throw createAuthError(
				error,
				"Password reset failed",
				"RESET_PASSWORD_FAILED",
			);
		}
	};

	const resendVerification = async (email: string) => {
		try {
			setState((prev) => ({ ...prev, isLoading: true, error: null }));

			const response = await resendVerificationMutation.mutateAsync({
				email,
			});

			setState((prev) => ({
				...prev,
				isLoading: false,
				error: null,
			}));

			return response;
		} catch (error) {
			const authErrorInfo = createAuthErrorInfo(
				error,
				"Resend verification failed",
				"RESEND_VERIFICATION_FAILED",
			);

			setState((prev) => ({
				...prev,
				isLoading: false,
				isAuthenticating: false,
				error: authErrorInfo,
			}));
			throw createAuthError(
				error,
				"Resend verification failed",
				"RESEND_VERIFICATION_FAILED",
			);
		}
	};

	const value: AuthContextType = {
		...state,
		login,
		verifyMfa,
		register,
		logout,
		refresh,
		verifyEmail,
		resendVerification,
		forgotPassword,
		resetPassword,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);

	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}

	return context;
}
